import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MUISelect, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { ReactNode, useEffect } from 'react';
import { Controller, FieldValues } from 'react-hook-form';

import { useTranslate } from '@/hooks/useTranslate';

import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';
import { getObjectFieldByPath } from '@/utils/string';

import { Icon } from '../../Icon/Icon';
import { TextInput } from '../TextInput';
import { SelectDesktopProps } from './Select.interface';
import {
  getSafeSelectValue,
  SelectHelperText,
  SelectSearchClearButton,
  SelectValue,
} from './SelectShared';
import { useSelectLogic } from './useSelectLogic';

import { FieldArrow, FieldArrowButton } from '../Form.styled';
import {
  DropdownOption,
  Label,
  OptionCheckIcon,
  SelectDrawerSearchBar,
  SelectedIcon,
  SelectGroupName,
  StyledFormControl,
} from './Select.styled';

interface SelectOptionListProps {
  options: Option[];
  multiple: boolean;
  maxSelections?: number;
  currentValue: unknown;
  showGroupNames: boolean;
  renderOption?: (
    option: Option,
    meta: { isSelected?: boolean; reachedMax?: boolean },
  ) => ReactNode;
  groups: {
    name: string;
    order: number;
    items: Option[];
  }[];
}

const renderOptionList = ({
  multiple,
  maxSelections,
  currentValue,
  renderOption,
  groups,
  showGroupNames,
}: SelectOptionListProps) => {
  const components: ReactNode[] = [];
  const isMultiple = !!multiple;
  let selectedValues: Array<unknown> = [];
  if (Array.isArray(currentValue)) {
    selectedValues = currentValue;
  } else if (currentValue !== undefined && currentValue !== null) {
    selectedValues = [currentValue];
  }

  groups.forEach(group => {
    if (showGroupNames && group.name) {
      components.push(
        <SelectGroupName key={group.name} className="group-name">
          {group.name === 'undefined' ? 'Other' : group.name}
        </SelectGroupName>,
      );
    }
    group.items.forEach(option => {
      const isSelected = selectedValues.includes(option.value);
      const reachedMax =
        isMultiple &&
        typeof maxSelections === 'number' &&
        selectedValues.length >= maxSelections &&
        !isSelected;
      components.push(
        <DropdownOption
          key={option.value}
          value={option.value}
          disabled={option.disabled || reachedMax}
          shouldMarginTop={!showGroupNames}
          {...getDataTestId(`select-option`)}
        >
          {renderOption ? (
            renderOption(option, { isSelected, reachedMax })
          ) : (
            <>
              {option.icon && (
                <ListItemIcon>
                  <Icon name={option.icon} size={20} />
                </ListItemIcon>
              )}
              <ListItemText className="dropdown-option-text">
                <Typography
                  component="span"
                  variant={isSelected ? 'bodyB2Strong' : 'bodyB2Regular'}
                >
                  {option.text || option.value}
                </Typography>
              </ListItemText>
              {isSelected && (
                <OptionCheckIcon>
                  <SelectedIcon name="check-mini" />
                </OptionCheckIcon>
              )}
            </>
          )}
        </DropdownOption>,
      );
    });
  });

  return components;
};

export function SelectDesktop<FormPayload extends FieldValues>({
  options,
  control,
  name,
  rules,
  helperText,
  withSearchBar = false,
  disabled = false,
  placeholder,
  maxSelections,
  onSelectChange,
  drawerOptionContainerSx: _drawerOptionContainerSx,
  drawerHeaderLayoutWidths: _drawerHeaderLayoutWidths,
  drawerTitle: _drawerTitle,
  renderOption,
  renderSelectedOption,
  keepGroupsWhenSearching = false,
  allowClearSearchText = false,
  ...props
}: SelectDesktopProps<FormPayload>) {
  const { t } = useTranslate();
  const {
    search,
    setSearch,
    isOpen,
    hide,
    show,
    optionByValue,
    groups: baseGroups,
    onSearchChange,
  } = useSelectLogic(options);

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen, setSearch]);

  const groups = (() => {
    const searchValue = search.trim();
    if (searchValue) {
      if (keepGroupsWhenSearching) {
        return baseGroups
          .map(group => ({
            ...group,
            items: group.items.filter(
              item =>
                item.text?.toLowerCase()?.includes(searchValue.toLowerCase()) ||
                String(item.value)
                  .toLowerCase()
                  .includes(searchValue.toLowerCase()),
            ),
          }))
          .filter(group => group.items.length > 0);
      }
      return [
        {
          name: '',
          order: 0,
          items: options.filter(
            item =>
              item.text?.toLowerCase()?.includes(searchValue.toLowerCase()) ||
              String(item.value)
                .toLowerCase()
                .includes(searchValue.toLowerCase()),
          ),
        },
      ];
    }
    return baseGroups;
  })();

  const showGroupNames =
    groups.length > 1 || (keepGroupsWhenSearching && !!search.trim());

  const onSelect = (onFieldChange: (data: unknown) => void) => {
    return (fieldValue: SelectChangeEvent<unknown>) => {
      onFieldChange(fieldValue);
      if (onSelectChange) {
        onSelectChange(fieldValue);
      }
    };
  };

  const onClickArrowButton = () => {
    if (disabled) return;
    if (isOpen) hide();
    else show();
  };

  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, formState }) => {
        const defaultFieldValue = getObjectFieldByPath(
          formState.defaultValues,
          name,
        );
        const safeValue = getSafeSelectValue({
          value,
          options,
          isMultiple: props.multiple,
        });
        const safeDefaultValue = getSafeSelectValue({
          value: defaultFieldValue,
          options,
          isMultiple: props.multiple,
        });

        const clearSearch = () => {
          setSearch('');
        };

        return (
          <StyledFormControl
            fullWidth={props.fullWidth}
            error={props.error}
            sx={props.sx}
          >
            {props.label && <Label error={props.error}>{props.label}</Label>}
            <MUISelect
              {...getDataTestId(`${name}-select`)}
              fullWidth
              displayEmpty={!!placeholder && !props.label}
              onChange={onSelect(onChange)}
              defaultValue={safeDefaultValue}
              onBlur={onBlur}
              value={safeValue}
              open={isOpen}
              onOpen={show}
              onClose={hide}
              disabled={disabled}
              multiple={props.multiple}
              IconComponent={() => (
                <FieldArrowButton onClick={onClickArrowButton}>
                  <FieldArrow name={isOpen ? 'arrow-up' : 'arrow-down'} />
                </FieldArrowButton>
              )}
              renderValue={selected => (
                <SelectValue
                  value={selected}
                  defaultValue={safeDefaultValue}
                  optionByValue={optionByValue}
                  placeholder={placeholder}
                  isMultiple={props.multiple}
                  optionsCount={options.length}
                  renderSelectedOption={renderSelectedOption}
                />
              )}
              {...props}
            >
              {withSearchBar && (
                <SelectDrawerSearchBar>
                  <TextInput
                    placeholder={t('common.search')}
                    template="search"
                    value={search}
                    onKeyDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                    onChange={onSearchChange}
                    autoComplete="off"
                    endAdornment={
                      allowClearSearchText && !!search ? (
                        <SelectSearchClearButton onClear={clearSearch} />
                      ) : undefined
                    }
                  />
                </SelectDrawerSearchBar>
              )}
              {renderOptionList({
                options,
                multiple: !!props.multiple,
                maxSelections,
                currentValue: safeValue,
                renderOption,
                groups,
                showGroupNames,
              })}
            </MUISelect>
            <SelectHelperText helperText={helperText} error={props.error} />
          </StyledFormControl>
        );
      }}
    />
  );
}
